import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { PhoneInput as ReactInternationalPhone } from 'react-international-phone';
import 'react-international-phone/style.css';
import classNames from 'classnames';
import { parsePhoneNumberFromString } from 'libphonenumber-js/max';

/* ---------- helpers ---------- */

function getCountryFromTimezone() {
   try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const timezoneToCountry = {
         // Africa
         'Africa/Johannesburg': 'ZA',
         'Africa/Cairo': 'EG',
         'Africa/Lagos': 'NG',
         'Africa/Nairobi': 'KE',
         'Africa/Casablanca': 'MA',
         'Africa/Tunis': 'TN',
         'Africa/Algiers': 'DZ',

         // Americas
         'America/New_York': 'US',
         'America/Chicago': 'US',
         'America/Denver': 'US',
         'America/Los_Angeles': 'US',
         'America/Phoenix': 'US',
         'America/Anchorage': 'US',
         'America/Toronto': 'CA',
         'America/Vancouver': 'CA',
         'America/Mexico_City': 'MX',
         'America/Sao_Paulo': 'BR',
         'America/Argentina/Buenos_Aires': 'AR',
         'America/Santiago': 'CL',
         'America/Lima': 'PE',
         'America/Bogota': 'CO',

         // Europe
         'Europe/London': 'GB',
         'Europe/Paris': 'FR',
         'Europe/Berlin': 'DE',
         'Europe/Rome': 'IT',
         'Europe/Madrid': 'ES',
         'Europe/Amsterdam': 'NL',
         'Europe/Brussels': 'BE',
         'Europe/Vienna': 'AT',
         'Europe/Zurich': 'CH',
         'Europe/Stockholm': 'SE',
         'Europe/Oslo': 'NO',
         'Europe/Copenhagen': 'DK',
         'Europe/Helsinki': 'FI',
         'Europe/Warsaw': 'PL',
         'Europe/Prague': 'CZ',
         'Europe/Budapest': 'HU',
         'Europe/Athens': 'GR',
         'Europe/Lisbon': 'PT',
         'Europe/Dublin': 'IE',
         'Europe/Moscow': 'RU',

         // Asia
         'Asia/Dubai': 'AE',
         'Asia/Karachi': 'PK',
         'Asia/Kolkata': 'IN',
         'Asia/Dhaka': 'BD',
         'Asia/Bangkok': 'TH',
         'Asia/Singapore': 'SG',
         'Asia/Hong_Kong': 'HK',
         'Asia/Shanghai': 'CN',
         'Asia/Tokyo': 'JP',
         'Asia/Seoul': 'KR',
         'Asia/Manila': 'PH',
         'Asia/Jakarta': 'ID',
         'Asia/Kuala_Lumpur': 'MY',
         'Asia/Ho_Chi_Minh': 'VN',
         'Asia/Tel_Aviv': 'IL',
         'Asia/Riyadh': 'SA',

         // Oceania
         'Australia/Sydney': 'AU',
         'Australia/Melbourne': 'AU',
         'Australia/Brisbane': 'AU',
         'Australia/Perth': 'AU',
         'Australia/Adelaide': 'AU',
         'Pacific/Auckland': 'NZ',
         'Pacific/Honolulu': 'US'
      };

      return timezoneToCountry[timezone] || 'ZA';
   } catch {
      return 'ZA';
   }
}

function formatToInternational(phoneNumber, countryCode) {
   if (!phoneNumber) return '';

   try {
      const cleaned = phoneNumber.replace(/[^\d+]/g, '');
      const phone = parsePhoneNumberFromString(cleaned, countryCode);

      if (phone && phone.isValid()) {
         return phone.number; // E.164
      }

      return cleaned.startsWith('+') ? cleaned : `+${cleaned}`;
   } catch {
      return phoneNumber;
   }
}

function isValidPhoneNumber(phoneNumber, countryCode, mobileOnly = false) {
   if (!phoneNumber) return false;

   try {
      const cleaned = phoneNumber.replace(/[^\d+]/g, '');
      const phone = parsePhoneNumberFromString(cleaned, countryCode);

      if (!phone || !phone.isValid()) return false;

      if (!mobileOnly) return true;

      const type = phone.getType?.();
      return type === 'MOBILE' || type === 'FIXED_LINE_OR_MOBILE';
   } catch {
      return false;
   }
}

/* ---------- component ---------- */

function PhoneInputComponent({
   className,
   value,
   controlRef,
   required = false,
   mobileOnly = true,
   onChange = () => {},
   name = 'phone',
   error,
   showMessageOnError = false,
   validateOnBlur = true,
   errorMessage = 'Please enter a valid mobile number',
   ...rest
}) {
   const detectedCountry = useMemo(() => getCountryFromTimezone(), []);
   const defaultCountry = detectedCountry.toLowerCase();

   const [phoneValue, setPhoneValue] = useState(value || '');
   const [touched, setTouched] = useState(false);
   const [internalValid, setInternalValid] = useState(true);
   const defaultError = 'Please enter a valid mobile number';
   const effectiveErrorMessage = !mobileOnly && errorMessage === defaultError ? 'Please enter a valid phone number' : errorMessage;

   const internalRef = useRef(null);
   const inputRef = controlRef || internalRef;

   useEffect(() => {
      if (value !== phoneValue) {
         setPhoneValue(value || '');
      }
   }, [value, phoneValue]);

   const handleChange = useCallback(
      (phone, meta) => {
         setPhoneValue(phone);

         const countryCode = (meta.country?.iso2 || defaultCountry).toUpperCase();

         const international = formatToInternational(phone, countryCode);
         const isValidPhoneNo = isValidPhoneNumber(phone, countryCode, mobileOnly);

         const isEmpty = international === '' || international === `+${meta.country?.dialCode}`;

         const isValid = required ? isValidPhoneNo : isEmpty || isValidPhoneNo;

         setInternalValid(isValid);

         onChange(
            {
               target: { name, value: international },
               currentTarget: { name, value: international }
            },
            international,
            {
               phone: isEmpty ? null : international,
               isValid,
               isValidPhoneNo,
               mobileOnly,
               isEmpty,
               country: meta.country,
               inputValue: phone,
               countryCode
            }
         );
      },
      [onChange, defaultCountry, name, required, mobileOnly]
   );

   const showError = Boolean(error) || (validateOnBlur && touched && !internalValid);

   return (
      <div
         className={classNames('PhoneInputWrapper', {
            'PhoneInputWrapper--error': showError
         })}
      >
         <ReactInternationalPhone
            ref={inputRef}
            value={phoneValue}
            onChange={handleChange}
            onBlur={() => setTouched(true)}
            defaultCountry={defaultCountry}
            className={classNames('PhoneInput', className, {
               'PhoneInput--error': showError
            })}
            aria-invalid={showError}
            {...rest}
         />

         {showError && showMessageOnError && (
            <div className='PhoneInputError' role='alert'>
               {typeof error === 'string' ? error : effectiveErrorMessage}
            </div>
         )}
      </div>
   );
}

export default PhoneInputComponent;
